import { useEffect, useMemo, useState } from "react";

import { getAdherentsByUser } from "../../../../services/adherentServices";
import {
  createEventByAdherent,
  deletePersonalAdherentEvent,
  getAdherentsByEvent,
  getEventsByAdherent,
  updateAdherentEventStatus,
  updateEvent,
} from "../../../../services/eventServices";

import {
  DEFAULT_FILTERS,
  EMPTY_EVENT_FORM,
  getFilteredEvents,
  hasEventResults,
  normalizeAdherentsResponse,
  normalizeCompetitorsResponse,
  normalizeEventForForm,
  normalizeEventsResponse,
  splitEventsByTime,
} from "../utils/eventsJudokaUtils";

export function useEventsJudoka({ addNotification }) {
  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [form, setForm] = useState(EMPTY_EVENT_FORM);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [invitationEvent, setInvitationEvent] = useState(null);
  const [resultsEvent, setResultsEvent] = useState(null);

  const [competitorsByEvent, setCompetitorsByEvent] = useState({});
  const [loadingCompetitors, setLoadingCompetitors] = useState({});

  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedAdherent = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find((adherent) => adherent.id === selectedAdherentId) ||
      adherents[0]
    );
  }, [adherents, selectedAdherentId]);

  const eventsByTime = useMemo(() => splitEventsByTime(events), [events]);

  const filteredEvents = useMemo(
    () => getFilteredEvents(eventsByTime, filters),
    [eventsByTime, filters],
  );

  const resultsCount = useMemo(
    () => events.filter((event) => hasEventResults(event)).length,
    [events],
  );

  const fetchAdherents = async () => {
    setLoading(true);

    try {
      const response = await getAdherentsByUser();
      const loadedAdherents = normalizeAdherentsResponse(response);

      setAdherents(loadedAdherents);

      if (loadedAdherents.length > 0) {
        setSelectedAdherentId((currentId) => {
          const stillExists = loadedAdherents.some(
            (adherent) => adherent.id === currentId,
          );

          return stillExists ? currentId : loadedAdherents[0].id;
        });
      } else {
        setSelectedAdherentId(null);
        setEvents([]);

        addNotification(
          "Aucun profil judoka trouvé. Créez d’abord un profil judoka.",
          "info",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);
      addNotification("Erreur lors de la récupération des adhérents.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async (adherentId) => {
    if (!adherentId) {
      setEvents([]);
      return;
    }

    setEventsLoading(true);

    try {
      const response = await getEventsByAdherent(adherentId);
      const loadedEvents = normalizeEventsResponse(response);

      setEvents(loadedEvents);
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);
      addNotification(
        "Erreur lors de la récupération des événements.",
        "error",
      );
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdherents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAdherentId) {
      fetchEvents(selectedAdherentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdherentId]);

  const selectAdherent = (adherentId) => {
    setSelectedAdherentId(adherentId);
    setCompetitorsByEvent({});
    setLoadingCompetitors({});
  };

  const openCreateModal = () => {
    setForm(EMPTY_EVENT_FORM);
    setSelectedEvent(null);
    setIsEditMode(false);
    setIsEventModalOpen(true);
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    setForm(normalizeEventForForm(event));
    setIsEditMode(true);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setIsEditMode(false);
    setSelectedEvent(null);
    setForm(EMPTY_EVENT_FORM);
    setSaving(false);
  };

  const openInvitationModal = (event) => {
    setInvitationEvent(event);
  };

  const closeInvitationModal = () => {
    setInvitationEvent(null);
  };

  const openResultsModal = (event) => {
    setResultsEvent(event);
  };

  const closeResultsModal = () => {
    setResultsEvent(null);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((prevForm) => ({
      ...prevForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildEventPayload = () => ({
    ...form,
    adherent_id: selectedAdherentId,

    // Compatibilité avec ton ancien système :
    // événement créé par le judoka = parent_event_id du judoka.
    parent_event_id: isEditMode
      ? selectedEvent?.parent_event_id || selectedAdherentId
      : selectedAdherentId,

    is_active: true,
    is_shared: Boolean(form.is_shared),
    cate_event: form.cate_event || "",
  });

  const handleSubmitEvent = async (event) => {
    event.preventDefault();

    if (!selectedAdherentId) {
      addNotification("Aucun judoka sélectionné.", "error");
      return;
    }

    if (!form.titre?.trim() || !form.start) {
      addNotification("Le titre et la date sont obligatoires.", "error");
      return;
    }

    setSaving(true);

    try {
      const payload = buildEventPayload();

      if (isEditMode && selectedEvent?.id) {
        await updateEvent(selectedEvent.id, payload);

        const updatedEvent = {
          ...selectedEvent,
          ...payload,
          id: selectedEvent.id,
        };

        setEvents((prevEvents) =>
          prevEvents.map((item) =>
            item.id === selectedEvent.id ? updatedEvent : item,
          ),
        );

        addNotification("Événement modifié avec succès.", "success");
      } else {
        const createdEvent = await createEventByAdherent(payload);

        const normalizedCreatedEvent = createdEvent?.id
          ? createdEvent
          : {
              ...payload,
              id: Date.now(),
              parent_event_id: selectedAdherentId,
            };

        setEvents((prevEvents) => [normalizedCreatedEvent, ...prevEvents]);

        addNotification("Événement créé avec succès.", "success");
      }

      closeEventModal();
      await fetchEvents(selectedAdherentId);
    } catch (error) {
      console.error("Erreur lors de l’enregistrement de l’événement :", error);
      addNotification(
        "Erreur lors de l’enregistrement de l’événement.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!event?.id) return;

    if (!selectedAdherentId) {
      addNotification("Aucun judoka sélectionné.", "error");
      return;
    }

    if (event.is_shared) {
      addNotification(
        "Impossible de supprimer un événement partagé depuis l'espace judoka.",
        "error",
      );
      return;
    }

    const linkedAdherentId =
      event?.AdherentEvent?.adherent_id ||
      event?.AdherentEvent?.adherentId ||
      selectedAdherentId;

    if (Number(linkedAdherentId) !== Number(selectedAdherentId)) {
      addNotification(
        "Cet événement n'est pas lié au judoka sélectionné.",
        "error",
      );
      return;
    }

    const confirmDelete = window.confirm(
      `Supprimer "${event.titre || "cet événement"}" de l'agenda de ce judoka ?\n\nCette action supprimera aussi les résultats, observations, podiums et tableaux liés à cet événement pour ce judoka.`,
    );

    if (!confirmDelete) return;

    try {
      await deletePersonalAdherentEvent(event.id, selectedAdherentId);

      setEvents((prevEvents) =>
        prevEvents.filter((item) => Number(item.id) !== Number(event.id)),
      );

      addNotification("Événement supprimé de l'agenda du judoka.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression de l’événement :", error);

      const message =
        error?.response?.data?.message ||
        "Erreur lors de la suppression de l’événement.";

      addNotification(message, "error");
    }
  };

  const handleStatusChange = async (adherentEvent, status) => {
    const adherentId = adherentEvent?.adherent_id;
    const eventId = adherentEvent?.event_id;

    if (!adherentId || !eventId) {
      addNotification(
        "Impossible de modifier le statut de cette invitation.",
        "error",
      );
      return;
    }

    try {
      await updateAdherentEventStatus(adherentId, eventId, status);

      setEvents((prevEvents) =>
        prevEvents.map((event) => {
          if (event.AdherentEvent?.event_id !== eventId) {
            return event;
          }

          return {
            ...event,
            AdherentEvent: {
              ...event.AdherentEvent,
              status,
            },
          };
        }),
      );

      addNotification(
        status === "accepted"
          ? "Inscription confirmée."
          : "Invitation refusée.",
        "success",
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut :", error);
      addNotification("Erreur lors de la mise à jour du statut.", "error");
    }
  };

  const fetchCompetitors = async (eventId) => {
    if (!eventId) return;

    setLoadingCompetitors((prev) => ({
      ...prev,
      [eventId]: true,
    }));

    try {
      const response = await getAdherentsByEvent(eventId);
      const competitors = normalizeCompetitorsResponse(response);

      setCompetitorsByEvent((prev) => ({
        ...prev,
        [eventId]: competitors,
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des inscrits :", error);
      addNotification("Erreur lors de la récupération des inscrits.", "error");
    } finally {
      setLoadingCompetitors((prev) => ({
        ...prev,
        [eventId]: false,
      }));
    }
  };

  const refreshEvents = () => {
    if (selectedAdherentId) {
      fetchEvents(selectedAdherentId);
    }
  };

  return {
    adherents,
    selectedAdherent,
    selectedAdherentId,

    events,
    eventsByTime,
    filteredEvents,
    filters,
    resultsCount,

    form,
    selectedEvent,

    isEventModalOpen,
    isEditMode,
    invitationEvent,
    resultsEvent,

    competitorsByEvent,
    loadingCompetitors,

    loading,
    eventsLoading,
    saving,

    selectAdherent,

    openCreateModal,
    openEditModal,
    closeEventModal,

    openInvitationModal,
    closeInvitationModal,

    openResultsModal,
    closeResultsModal,

    handleFilterChange,
    resetFilters,

    handleInputChange,
    handleSubmitEvent,
    handleDeleteEvent,
    handleStatusChange,

    fetchCompetitors,
    refreshEvents,
  };
}
